import { chavePrimaria, uuidv7 } from './ids.js';
import {
  MOTIVO_SEM_PERMISSAO,
  type LocalStore,
  type OutboxItem,
  type RemoteChange,
  type SyncReport,
  type SyncTransport,
} from './types.js';

export interface SyncOptions {
  pushBatchSize?: number;
  pullBatchSize?: number;
}

/*
  Falha de rede nunca manda item para a quarentena, por mais que se repita: a
  fila so esvazia com o servidor confirmando. Quarentena e so para recusa.
*/
const DEFAULTS = { pushBatchSize: 100, pullBatchSize: 500 } as const;

/**
 * O motor de sincronizacao.
 *
 * Push primeiro, pull depois — sempre nessa ordem. O dado do dispositivo e o que
 * pode se perder; o do servidor ja esta salvo. Se so uma das duas metades rodar
 * antes de a rede cair, que seja a que protege o dia de coleta.
 */
export class SyncEngine {
  private readonly opts: Required<SyncOptions>;
  private running = false;
  /**
   * createdAt estritamente crescente: a fila sai por createdAt, e as chaves
   * estrangeiras dependem da ordem (visita antes das avaliacoes gravadas no
   * mesmo milissegundo).
   */
  private ultimoCreatedAt = 0;

  constructor(
    private readonly store: LocalStore,
    private readonly transport: SyncTransport,
    options: SyncOptions = {},
  ) {
    this.opts = { ...DEFAULTS, ...options };
  }

  /** Enfileira uma mutacao local. Nunca escreve na rede. */
  async enqueue(item: Omit<OutboxItem, 'createdAt' | 'attempts' | 'lastError'>): Promise<void> {
    await this.store.enqueue({ ...item, createdAt: Date.now(), attempts: 0, lastError: null });
  }

  /**
   * Registra uma escrita feita no aparelho: a linha aparece na tela na hora e o
   * item vai para a fila, na mesma transacao. E o caminho de toda tela de
   * cadastro e coleta. Devolve o clientMutationId.
   */
  async registrar(
    entity: string,
    entityId: string,
    payload: Record<string, unknown>,
    op: OutboxItem['op'] = 'upsert',
  ): Promise<string> {
    const agora = Math.max(Date.now(), this.ultimoCreatedAt + 1);
    this.ultimoCreatedAt = agora;
    const clientMutationId = uuidv7(agora);
    await this.store.gravarLocal({
      clientMutationId,
      entity,
      entityId,
      op,
      // A chave vai no payload: o servidor faz upsert pelas colunas do payload.
      payload:
        op === 'delete'
          ? {}
          : { ...payload, [chavePrimaria(entity)]: entityId, updated_at: new Date(agora).toISOString() },
      createdAt: agora,
      attempts: 0,
      lastError: null,
    });
    return clientMutationId;
  }

  /**
   * Uma rodada completa. Reentrante por guarda simples: duas rodadas simultaneas
   * mandariam o mesmo lote duas vezes — inofensivo pela idempotencia, mas
   * desperdicaria a janela de rede, que no campo e curta.
   */
  async sync(): Promise<SyncReport> {
    if (this.running) {
      return {
        pushed: 0, duplicates: 0, rejected: 0, pulled: 0,
        pending: await this.store.pendingCount(),
        cursor: (await this.store.getSyncState()).lastServerSeq,
        errors: ['sincronizacao ja em andamento'],
      };
    }
    this.running = true;
    try {
      const push = await this.pushAll();
      const pull = await this.pullAll();
      const errors = [...push.errors, ...pull.errors];
      // O resultado fica gravado: e o que a tela de sincronizacao mostra, mesmo
      // depois de o app ser fechado.
      const state = await this.store.getSyncState();
      await this.store.setSyncState({
        ...state,
        lastErrors: errors,
        lastSyncAt: errors.length === 0 ? Date.now() : state.lastSyncAt,
      });
      return {
        ...push,
        pulled: pull.pulled,
        cursor: pull.cursor,
        pending: await this.store.pendingCount(),
        errors,
      };
    } finally {
      this.running = false;
    }
  }

  /**
   * Emenda de ficha fechada: sobe a `amendment` (o servidor confere o valor
   * antigo e aplica) e reflete a correcao na tela na hora, so no aparelho.
   */
  async emendar(
    e: {
      entity: string;
      entityId: string;
      field: string;
      valorAntigo: string | null;
      valorNovo: string | null;
      motivo: string;
      autor: string | null;
    },
    reflexoLocal: Record<string, unknown>,
  ): Promise<string> {
    const id = uuidv7();
    await this.registrar('amendment', id, {
      entity: e.entity,
      entity_id: e.entityId,
      field: e.field,
      old_value: e.valorAntigo,
      new_value: e.valorNovo,
      reason: e.motivo.trim(),
      actor_id: e.autor,
      at: new Date().toISOString(),
    });
    await this.store.gravarSoLocal(e.entity, e.entityId, reflexoLocal);
    return id;
  }

  /**
   * Volta um item da quarentena para a fila, depois de resolvida a causa. O
   * `ajuste` corrige o conteudo antes do reenvio (ex.: o numero da visita que
   * outro tablet ja usou) e vale tambem na linha deste aparelho.
   */
  async tentarDeNovo(quarentenaId: number, ajuste?: Record<string, unknown>): Promise<void> {
    if (ajuste) {
      const item = (await this.store.listQuarantine()).find((q) => q.id === quarentenaId)?.item;
      if (item) await this.store.gravarSoLocal(item.entity, item.entityId, ajuste);
    }
    await this.store.requeueFromQuarantine(quarentenaId, ajuste);
  }

  /**
   * Abre mao de um item recusado. A linha local ainda mostra o valor recusado;
   * ela e apagada e o cursor volta ao zero, e o proximo pull a reconstroi como o
   * servidor a tem. Se o servidor nunca a teve (criacao recusada), ela some.
   */
  async descartar(quarentenaId: number): Promise<void> {
    await this.store.discardFromQuarantine(quarentenaId);
    const state = await this.store.getSyncState();
    await this.store.setSyncState({ ...state, lastServerSeq: 0 });
  }

  private async pushAll() {
    let pushed = 0;
    let duplicates = 0;
    let rejected = 0;
    const errors: string[] = [];
    const state = await this.store.getSyncState();

    for (;;) {
      const batch = await this.store.peekOutbox(this.opts.pushBatchSize);
      if (batch.length === 0) break;

      let result;
      try {
        result = await this.transport.push(batch, state.deviceId);
      } catch (e) {
        // Rede caiu no meio. O outbox fica intacto: nada e perdido, e a proxima
        // rodada reenvia. A idempotencia do servidor cobre o reenvio.
        const msg = e instanceof Error ? e.message : String(e);
        errors.push(`push falhou: ${msg}`);
        for (const item of batch) await this.store.recordFailure(item.clientMutationId, msg);
        break;
      }

      const byId = new Map(batch.map((i) => [i.clientMutationId, i]));
      const resolved: string[] = [];
      let semPermissao = false;

      for (const outcome of result.results) {
        const item = byId.get(outcome.clientMutationId);
        if (outcome.status === 'rejected' && outcome.reason === MOTIVO_SEM_PERMISSAO) {
          // Problema do cadastro da pessoa, nao do dado: o item fica na fila.
          semPermissao = true;
          await this.store.recordFailure(outcome.clientMutationId, MOTIVO_SEM_PERMISSAO);
          continue;
        }
        if (outcome.status === 'rejected') {
          rejected++;
          // Rejeicao nao se resolve tentando de novo: ou a ficha ja fechou, ou a
          // entidade nao e sincronizavel. Vai para a quarentena, onde uma pessoa
          // decide. Sobrescrever em silencio seria pior que travar.
          if (item) await this.store.quarantine(item, outcome.reason ?? 'rejeitado pelo servidor');
          resolved.push(outcome.clientMutationId);
          continue;
        }
        if (outcome.status === 'duplicate') duplicates++;
        else pushed++;
        resolved.push(outcome.clientMutationId);
      }

      await this.store.dropFromOutbox(resolved);

      if (semPermissao) {
        errors.push(`push falhou: ${MOTIVO_SEM_PERMISSAO}`);
        break;
      }
      // Nenhum item saiu do outbox: sem isso o laco gira para sempre.
      if (resolved.length === 0) {
        errors.push('servidor nao confirmou nenhuma mutacao do lote');
        break;
      }
      if (batch.length < this.opts.pushBatchSize) break;
    }

    // "Ultimo envio" so conta envio que deu certo.
    if (errors.length === 0) await this.store.setSyncState({ ...(await this.store.getSyncState()), lastPushAt: Date.now() });
    return { pushed, duplicates, rejected, errors };
  }

  private async pullAll() {
    let pulled = 0;
    const errors: string[] = [];
    let state = await this.store.getSyncState();
    let cursor = state.lastServerSeq;

    for (;;) {
      let result;
      try {
        result = await this.transport.pull(cursor, this.opts.pullBatchSize);
      } catch (e) {
        errors.push(`pull falhou: ${e instanceof Error ? e.message : String(e)}`);
        break;
      }

      if (result.changes.length > 0) {
        await this.store.applyRemote(result.changes);
        pulled += result.changes.length;
      }

      // O cursor e um inteiro do servidor, nunca um horario: tablet com relogio
      // errado nao quebra a sincronizacao.
      if (result.cursor <= cursor) break;
      cursor = result.cursor;

      state = { ...state, lastServerSeq: cursor, lastPullAt: Date.now() };
      await this.store.setSyncState(state);

      if (!result.hasMore) break;
    }

    return { pulled, cursor, errors };
  }
}

export type { RemoteChange };
